import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Books, MagnifyingGlass, Tag, Heart } from "@phosphor-icons/react";
import { useFavoritos } from "@/utils/favoritos";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const fmtPrecio = (n) => {
  const num = Number(n) || 0;
  return `$${num % 1 === 0 ? num.toLocaleString("es-MX") : num.toFixed(2)}`;
};

const LibreriaPage = () => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");
  const [favLibros, toggleFavLibro] = useFavoritos("libros");

  const marcarFavLibro = (b) => {
    const activo = toggleFavLibro(b.id);
    toast.success(activo ? `"${b.titulo}" guardado en favoritos` : "Quitado de favoritos");
  };

  useEffect(() => {
    document.title = "Librería — Viento de Gracia | Libros cristianos en Toluca";
    axios.get(`${API}/books`).then(({ data }) => setBooks(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const allTags = useMemo(() => {
    const set = new Set();
    books.forEach((b) => (b.tags || []).forEach((t) => set.add(t)));
    return [...set].sort();
  }, [books]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return books.filter((b) => {
      if (tag === "__fav" && !favLibros.includes(b.id)) return false;
      if (tag && tag !== "__fav" && !(b.tags || []).includes(tag)) return false;
      if (!needle) return true;
      return (
        b.titulo.toLowerCase().includes(needle) ||
        (b.autor || "").toLowerCase().includes(needle) ||
        (b.tags || []).some((t) => t.includes(needle))
      );
    });
  }, [books, q, tag, favLibros]);

  return (
    <div className="min-h-screen bg-ink text-paper" data-testid="libreria-page">
      <div className="grain-overlay" />

      <header className="flex items-center justify-between border-b border-white/10 px-6 py-5 lg:px-12">
        <a href="/" className="flex items-center gap-3" data-testid="libreria-back-home">
          <img src="/logo-mark-oficial.png" alt="Viento de Gracia" className="h-9 w-9 object-contain" />
          <span className="font-serif text-sm uppercase tracking-[0.3em]">Viento de <em className="italic text-gold">Gracia</em></span>
        </a>
        <a href="/" className="flex items-center gap-2 text-[10px] font-light uppercase tracking-[0.25em] text-paper/60 transition-colors hover:text-fire" data-testid="libreria-volver">
          <ArrowLeft size={14} weight="light" /> Volver al sitio
        </a>
      </header>

      <main className="mx-auto max-w-[1600px] px-6 py-16 lg:px-12 lg:py-24">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
          <p className="mb-8 flex items-center gap-4 text-[10px] font-light uppercase tracking-[0.5em] text-fire" data-testid="libreria-eyebrow">
            <span className="inline-block h-px w-16 bg-fire" /> Librería · Cada compra apoya la obra
          </p>
          <h1 className="font-serif uppercase leading-[0.85] tracking-tight" data-testid="libreria-title">
            <span className="block text-[13vw] lg:text-8xl">Libros que</span>
            <span className="block text-[13vw] italic text-gold lg:text-8xl">edifican</span>
          </h1>
          <p className="mt-8 max-w-lg text-sm font-light leading-relaxed tracking-wide text-smoke" data-testid="libreria-desc">
            Títulos cristianos seleccionados por nuestro equipo pastoral. Recógelos en la iglesia o pídelos a domicilio —
            todo lo recaudado sostiene los ministerios de Viento de Gracia.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="mt-14 flex flex-wrap items-end gap-6 border border-white/10 bg-surface p-6 lg:p-8"
          data-testid="libreria-filters"
        >
          <div className="min-w-64 flex-1">
            <p className="mb-2 flex items-center gap-2 text-[10px] font-light uppercase tracking-[0.3em] text-smoke">
              <MagnifyingGlass size={13} weight="light" /> Buscar
            </p>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Título, autor o tema..."
              className="w-full border-b border-white/15 bg-transparent py-3 text-sm font-light tracking-wide text-paper placeholder:text-paper/35 transition-colors duration-300 focus:border-fire focus:outline-none"
              data-testid="libreria-search"
            />
          </div>
          {allTags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="libreria-tags">
              <Tag size={14} weight="light" className="text-smoke" />
              <button
                onClick={() => setTag("")}
                data-testid="libreria-tag-todos"
                className={`border px-4 py-1.5 text-[9px] font-light uppercase tracking-[0.2em] transition-colors ${!tag ? "border-fire bg-fire/10 text-fire" : "border-white/15 text-smoke hover:border-paper hover:text-paper"}`}
              >
                Todos
              </button>
              <button
                onClick={() => setTag((cur) => (cur === "__fav" ? "" : "__fav"))}
                data-testid="libreria-tag-favoritos"
                className={`flex items-center gap-1.5 border px-4 py-1.5 text-[9px] font-light uppercase tracking-[0.2em] transition-colors ${tag === "__fav" ? "border-fire bg-fire/10 text-fire" : "border-white/15 text-smoke hover:border-paper hover:text-paper"}`}
              >
                <Heart size={11} weight={tag === "__fav" ? "fill" : "light"} /> Favoritos ({favLibros.filter((id) => books.some((b) => b.id === id)).length})
              </button>
              {allTags.map((t) => (
                <button
                  key={t}
                  onClick={() => setTag((cur) => (cur === t ? "" : t))}
                  data-testid={`libreria-tag-${t}`}
                  className={`border px-4 py-1.5 text-[9px] font-light uppercase tracking-[0.2em] transition-colors ${tag === t ? "border-fire bg-fire/10 text-fire" : "border-white/15 text-smoke hover:border-paper hover:text-paper"}`}
                >
                  {t}
                </button>
              ))}
            </div>
          )}
        </motion.div>

        {loading ? (
          <p className="py-24 text-center font-serif text-2xl italic text-smoke" data-testid="libreria-loading">Abriendo la librería...</p>
        ) : filtered.length === 0 ? (
          <div className="mt-12 border border-white/10 bg-surface p-16 text-center" data-testid="libreria-empty">
            <Books size={40} weight="light" className="mx-auto text-fire" />
            <p className="mt-6 font-serif text-3xl italic text-smoke">
              {books.length === 0 ? "Estamos surtiendo los estantes" : "Ningún libro coincide con tu búsqueda"}
            </p>
            <p className="mt-3 text-[10px] font-light uppercase tracking-[0.3em] text-smoke/70">
              {books.length === 0 ? "Muy pronto encontrarás aquí los títulos de la casa" : "Prueba con otra palabra o quita los filtros"}
            </p>
          </div>
        ) : (
          <div className="mt-12 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4" data-testid="libreria-grid">
            {filtered.map((b, i) => (
              <motion.a
                key={b.id}
                href={`/libreria/${b.slug}`}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.05 * Math.min(i, 8), ease: [0.16, 1, 0.3, 1] }}
                className="group relative flex flex-col bg-surface"
                data-testid={`book-card-${b.slug}`}
              >
                <div className="relative overflow-hidden">
                  <img
                    src={`${BACKEND_URL}${b.portada}`}
                    alt={`Portada del libro ${b.titulo}`}
                    className={`aspect-[3/4] w-full object-cover transition-transform duration-700 group-hover:scale-[1.04] ${b.stock <= 0 ? "opacity-40 grayscale" : ""}`}
                    loading="lazy"
                  />
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); marcarFavLibro(b); }}
                    data-testid={`book-fav-${b.slug}`}
                    aria-label={`Guardar ${b.titulo} en favoritos`}
                    className={`absolute right-3 top-3 flex h-9 w-9 items-center justify-center border backdrop-blur-sm transition-colors duration-300 ${favLibros.includes(b.id) ? "border-fire bg-ink/70 text-fire" : "border-white/20 bg-ink/50 text-paper/70 hover:text-fire"}`}
                  >
                    <Heart size={16} weight={favLibros.includes(b.id) ? "fill" : "light"} />
                  </button>
                  {b.destacado && (
                    <span className="absolute left-0 top-4 bg-fire px-3 py-1 text-[9px] font-medium uppercase tracking-[0.2em] text-ink" data-testid={`book-destacado-${b.slug}`}>
                      Destacado
                    </span>
                  )}
                  {b.stock <= 0 && (
                    <span className="absolute inset-0 flex items-center justify-center" data-testid={`book-agotado-${b.slug}`}>
                      <span className="border border-paper/40 bg-ink/70 px-5 py-2 text-[10px] font-light uppercase tracking-[0.3em] text-paper">Agotado</span>
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <p className="font-serif text-xl leading-snug text-paper transition-colors duration-300 group-hover:text-fire">{b.titulo}</p>
                  {b.autor && <p className="mt-1 text-xs font-light text-smoke">{b.autor}</p>}
                  <div className="mt-auto flex items-center justify-between pt-5">
                    <span className="flex items-center gap-2" data-testid={`book-precio-${b.slug}`}>
                      {b.precio_oferta > 0 && b.precio_oferta < b.precio ? (
                        <>
                          <span className="font-serif text-lg text-fire">{fmtPrecio(b.precio_oferta)}</span>
                          <span className="text-xs font-light text-smoke line-through">{fmtPrecio(b.precio)}</span>
                        </>
                      ) : (
                        <span className="font-serif text-lg text-fire">{fmtPrecio(b.precio)}</span>
                      )}
                    </span>
                    <span className="text-[9px] font-light uppercase tracking-[0.25em] text-smoke transition-colors group-hover:text-paper">Ver libro →</span>
                  </div>
                </div>
              </motion.a>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default LibreriaPage;
